import { BaseService } from './BaseService';
import { apiService } from './apiService';
import {
  TestResult,
  TestStatistics,
  AgentSnapshot,
  TestScenario,
  TestReport,
  GetTestResultsParams,
  GetTestResultsResponse,
  GetTestResultParams,
  AddCommentRequest,
  AddCommentResponse,
  UpdateCommentRequest,
  UpdateCommentResponse,
  DeleteCommentResponse,
  GetStatisticsParams,
  GetAgentSnapshotsParams,
  GetAgentSnapshotsResponse,
  GetScenariosParams,
  GetScenariosResponse,
  GetReportsParams,
  GetReportsResponse,
} from '../types/testEvaluation';

export class TestEvaluationService extends BaseService {
  constructor() {
    super('TestEvaluationService', { debug: false });
  }

  protected onCircuitBreakerChange(eventType: string, data: any): void {
    this.logDebug('Circuit breaker event', { eventType, data });
  }

  protected onRetry(error: Error, attempt: number, delay: number, operationName: string): void {
    this.logDebug('Retrying operation', { operationName, attempt, delay, error: error.message });
  }

  /**
   * Get test results with optional filtering and pagination
   */
  async getTestResults(params: GetTestResultsParams = {}): Promise<GetTestResultsResponse> {
    return this.executeOperation(async () => {
      const queryParams = new URLSearchParams();

      if (params.agentId) queryParams.append('agentId', params.agentId);
      if (params.scenarioId) queryParams.append('scenarioId', params.scenarioId);
      if (params.passed !== undefined) queryParams.append('passed', params.passed.toString());
      if (params.category) queryParams.append('category', params.category);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.maxItems) queryParams.append('maxItems', params.maxItems.toString());
      if (params.continuationToken) queryParams.append('continuationToken', params.continuationToken);

      const endpoint = `/api/test-evaluation/test-results${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      return await apiService.get<GetTestResultsResponse>(endpoint);
    }, 'getTestResults');
  }

  /**
   * Get a single test result by ID
   */
  async getTestResult(params: GetTestResultParams): Promise<TestResult> {
    return this.executeOperation(async () => {
      const endpoint = `/api/test-evaluation/test-results/${params.id}`;
      
      return await apiService.get<TestResult>(endpoint);
    }, 'getTestResult');
  }

  /**
   * Add a reviewer comment to a test result
   */
  async addComment(testResultId: string, request: AddCommentRequest): Promise<AddCommentResponse> {
    return this.executeOperation(async () => {
      const endpoint = `/api/test-evaluation/test-results/${testResultId}/comments`;
      
      return await apiService.post<AddCommentResponse>(endpoint, request);
    }, 'addComment');
  }

  /**
   * Update an existing comment (only by comment author)
   */
  async updateComment(
    testResultId: string,
    commentId: string,
    request: UpdateCommentRequest
  ): Promise<UpdateCommentResponse> {
    return this.executeOperation(async () => {
      const endpoint = `/api/test-evaluation/test-results/${testResultId}/comments/${commentId}`;
      
      return await apiService.put<UpdateCommentResponse>(endpoint, request);
    }, 'updateComment');
  }

  /**
   * Delete a comment (only by comment author)
   */
  async deleteComment(testResultId: string, commentId: string): Promise<DeleteCommentResponse> {
    return this.executeOperation(async () => {
      const endpoint = `/api/test-evaluation/test-results/${testResultId}/comments/${commentId}`;
      
      return await apiService.delete<DeleteCommentResponse>(endpoint);
    }, 'deleteComment');
  }

  /**
   * Get aggregated test statistics
   */
  async getStatistics(params: GetStatisticsParams = {}): Promise<TestStatistics> {
    return this.executeOperation(async () => {
      const queryParams = new URLSearchParams();

      if (params.agentId) queryParams.append('agentId', params.agentId);
      if (params.category) queryParams.append('category', params.category);
      if (params.passed !== undefined) queryParams.append('passed', params.passed.toString());
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const endpoint = `/api/test-evaluation/statistics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      return await apiService.get<TestStatistics>(endpoint);
    }, 'getStatistics');
  }

  /**
   * Get agent configuration snapshots
   */
  async getAgentSnapshots(params: GetAgentSnapshotsParams = {}): Promise<GetAgentSnapshotsResponse> {
    return this.executeOperation(async () => {
      const queryParams = new URLSearchParams();

      if (params.agentId) queryParams.append('agentId', params.agentId);
      if (params.maxItems) queryParams.append('maxItems', params.maxItems.toString());
      if (params.continuationToken) queryParams.append('continuationToken', params.continuationToken);

      const endpoint = `/api/test-evaluation/agent-snapshots${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      return await apiService.get<GetAgentSnapshotsResponse>(endpoint);
    }, 'getAgentSnapshots');
  }

  /**
   * Get test scenarios
   */
  async getScenarios(params: GetScenariosParams = {}): Promise<GetScenariosResponse> {
    return this.executeOperation(async () => {
      const queryParams = new URLSearchParams();

      if (params.scenarioId) queryParams.append('scenarioId', params.scenarioId);
      if (params.category) queryParams.append('category', params.category);
      if (params.maxItems) queryParams.append('maxItems', params.maxItems.toString());
      if (params.continuationToken) queryParams.append('continuationToken', params.continuationToken);

      const endpoint = `/api/test-evaluation/scenarios${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      return await apiService.get<GetScenariosResponse>(endpoint);
    }, 'getScenarios');
  }

  /**
   * Get test reports
   */
  async getReports(params: GetReportsParams = {}): Promise<GetReportsResponse> {
    return this.executeOperation(async () => {
      const queryParams = new URLSearchParams();

      if (params.reportType) queryParams.append('reportType', params.reportType);
      if (params.maxItems) queryParams.append('maxItems', params.maxItems.toString());
      if (params.continuationToken) queryParams.append('continuationToken', params.continuationToken);

      const endpoint = `/api/test-evaluation/reports${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      return await apiService.get<GetReportsResponse>(endpoint);
    }, 'getReports');
  }

  /**
   * Get a single test report by ID
   */
  async getReport(id: string, reportType: string = 'test_execution_summary'): Promise<TestReport> {
    return this.executeOperation(async () => {
      const queryParams = new URLSearchParams();
      queryParams.append('reportType', reportType);

      const endpoint = `/api/test-evaluation/reports/${id}?${queryParams.toString()}`;
      
      return await apiService.get<TestReport>(endpoint);
    }, 'getReport');
  }

  /**
   * Get a single agent snapshot by ID
   */
  async getAgentSnapshot(id: string, agentId: string): Promise<AgentSnapshot> {
    return this.executeOperation(async () => {
      const queryParams = new URLSearchParams();
      queryParams.append('agentId', agentId);

      const endpoint = `/api/test-evaluation/agent-snapshots/${id}?${queryParams.toString()}`;
      
      return await apiService.get<AgentSnapshot>(endpoint);
    }, 'getAgentSnapshot');
  }

  /**
   * Get a single scenario by ID
   */
  async getScenario(id: string, scenarioType: string): Promise<TestScenario> {
    return this.executeOperation(async () => {
      const queryParams = new URLSearchParams();
      queryParams.append('scenarioType', scenarioType);

      const endpoint = `/api/test-evaluation/scenarios/${id}?${queryParams.toString()}`;
      
      return await apiService.get<TestScenario>(endpoint);
    }, 'getScenario');
  }
}

export const testEvaluationService = new TestEvaluationService();
